import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shield, KeyRound, Trash2 } from 'lucide-react';
import AdminReviewFilters, {
  AdminReviewFilterState,
  itemMatchesTab,
} from '../components/admin/AdminReviewFilters';
import AdminReviewList from '../components/admin/AdminReviewList';
import AdminReviewDetailPanel from '../components/admin/AdminReviewDetailPanel';
import AdminReviewDecisionModal, {
  DecisionFormValues,
} from '../components/admin/AdminReviewDecisionModal';
import {
  AdminReviewApiError,
  discoverReviewItems,
  decideReviewItem,
  evaluateReviewItem,
  getReviewItem,
  listReviewItems,
  markDelivered,
  claimReviewItem,
  requestFeedback,
  scanReviewItem,
  type ReviewDecision,
  type ReviewItem,
  type ScanResponse,
} from '../lib/adminReviewApi';
import {
  clearAdminKey,
  getAdminKey,
  hasAdminKey,
  setAdminKey,
} from '../lib/adminKeyStorage';

const DEFAULT_FILTERS: AdminReviewFilterState = {
  tab: 'needs_review',
  workflowStatus: '',
  deliveryStatus: '',
  autoGateStatus: '',
  searchQuery: '',
  limit: 50,
};

const AdminReviewQueue: React.FC = () => {
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminKeyReady, setAdminKeyReady] = useState(hasAdminKey());
  const [filters, setFilters] = useState<AdminReviewFilterState>(DEFAULT_FILTERS);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastScanResult, setLastScanResult] = useState<ScanResponse | null>(null);
  const [lastEvaluateSummary, setLastEvaluateSummary] = useState<Record<string, unknown> | null>(
    null
  );
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<ReviewDecision | null>(null);
  const [discoverLimit, setDiscoverLimit] = useState(10);
  const [discoverUploadId, setDiscoverUploadId] = useState('');
  const [discoverResult, setDiscoverResult] = useState<string | null>(null);

  const visibleItems = useMemo(
    () => items.filter((item) => itemMatchesTab(item, filters.tab)),
    [items, filters.tab]
  );

  const handleApiError = useCallback((error: unknown) => {
    if (error instanceof AdminReviewApiError) {
      if (error.status === 401 || error.status === 403) {
        setAdminKeyReady(false);
      }
      setErrorMessage(error.message);
      return;
    }
    if (error instanceof Error) {
      setErrorMessage(error.message);
      return;
    }
    setErrorMessage('Unexpected error. Please retry.');
  }, []);

  const refreshList = useCallback(async () => {
    if (!hasAdminKey()) {
      setAdminKeyReady(false);
      setErrorMessage('Admin key missing or invalid');
      return;
    }

    setListLoading(true);
    setErrorMessage(null);
    try {
      const response = await listReviewItems({
        workflow_status: filters.workflowStatus || undefined,
        delivery_status: filters.deliveryStatus || undefined,
        auto_gate_status: filters.autoGateStatus || undefined,
        q: filters.searchQuery || undefined,
        limit: filters.limit,
        include_internal: false,
      });
      setItems(response.items);
    } catch (error) {
      handleApiError(error);
    } finally {
      setListLoading(false);
    }
  }, [filters, handleApiError]);

  const loadDetail = useCallback(
    async (uploadId: string) => {
      if (!hasAdminKey()) return;
      setDetailLoading(true);
      setErrorMessage(null);
      try {
        const item = await getReviewItem(uploadId, true);
        setSelectedItem(item);
        setLastScanResult(null);
        setLastEvaluateSummary(null);
      } catch (error) {
        handleApiError(error);
      } finally {
        setDetailLoading(false);
      }
    },
    [handleApiError]
  );

  useEffect(() => {
    if (adminKeyReady) {
      refreshList();
    }
  }, [adminKeyReady, refreshList]);

  useEffect(() => {
    if (selectedUploadId && adminKeyReady) {
      loadDetail(selectedUploadId);
    } else {
      setSelectedItem(null);
    }
  }, [selectedUploadId, adminKeyReady, loadDetail]);

  const saveAdminKey = () => {
    const trimmed = adminKeyInput.trim();
    if (!trimmed) {
      setErrorMessage('Enter an admin key to continue.');
      return;
    }
    setAdminKey(trimmed);
    setAdminKeyInput('');
    setAdminKeyReady(true);
    setErrorMessage(null);
  };

  const removeAdminKey = () => {
    clearAdminKey();
    setAdminKeyReady(false);
    setItems([]);
    setSelectedUploadId(null);
    setSelectedItem(null);
    setErrorMessage(null);
  };

  const handleSelect = (uploadId: string) => {
    setSelectedUploadId(uploadId);
  };

  const runEvaluate = async () => {
    if (!selectedUploadId) return;
    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await evaluateReviewItem(selectedUploadId);
      setSelectedItem(response.item);
      setLastEvaluateSummary(response.decision);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const runScan = async () => {
    if (!selectedUploadId) return;
    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await scanReviewItem(selectedUploadId, {
        run_projection: true,
        run_forbidden_copy: true,
      });
      setSelectedItem(response.item);
      setLastScanResult(response);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const runClaim = async (reviewer: string) => {
    if (!selectedUploadId) return;
    setActionLoading(true);
    setErrorMessage(null);
    try {
      const item = await claimReviewItem(selectedUploadId, reviewer);
      setSelectedItem(item);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const openDecisionModal = (decision: ReviewDecision) => {
    setPendingDecision(decision);
    setDecisionModalOpen(true);
  };

  const submitDecision = async (values: DecisionFormValues) => {
    if (!selectedUploadId || !pendingDecision) return;
    setActionLoading(true);
    setErrorMessage(null);
    try {
      const item = await decideReviewItem(selectedUploadId, {
        decision: pendingDecision,
        reviewer: values.reviewer,
        review_notes_internal: values.review_notes_internal || undefined,
        customer_safe_summary: values.customer_safe_summary || undefined,
        hold_reason: values.hold_reason || undefined,
        checklist: {},
      });
      setSelectedItem(item);
      setDecisionModalOpen(false);
      setPendingDecision(null);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmMarkDelivered = async () => {
    if (!selectedUploadId) return;
    const confirmed = window.confirm(
      'Mark this review item as delivered?\n\nThis records manual delivery only. It does not send an email.'
    );
    if (!confirmed) return;

    setActionLoading(true);
    setErrorMessage(null);
    try {
      const item = await markDelivered(selectedUploadId, {
        delivered_by: getAdminKey() ? 'admin-ui' : undefined,
      });
      setSelectedItem(item);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmRequestFeedback = async () => {
    if (!selectedUploadId) return;
    const confirmed = window.confirm(
      'Record a feedback request for this item?\n\nThis records feedback request only. It does not send an email.'
    );
    if (!confirmed) return;

    setActionLoading(true);
    setErrorMessage(null);
    try {
      const item = await requestFeedback(selectedUploadId);
      setSelectedItem(item);
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const runDiscover = async (dryRun: boolean, createMissing: boolean) => {
    if (!dryRun && createMissing) {
      const confirmed = window.confirm(
        'Create Missing writes review_items documents in Firestore.\n\nContinue?'
      );
      if (!confirmed) return;
    }

    setActionLoading(true);
    setDiscoverResult(null);
    setErrorMessage(null);
    try {
      const response = await discoverReviewItems({
        dry_run: dryRun,
        create_missing: createMissing,
        limit: discoverLimit,
        upload_id: discoverUploadId.trim() || undefined,
      });
      setDiscoverResult(
        dryRun
          ? `Dry-run: would create ${response.would_create_count} item(s).`
          : `Created ${response.created_count} item(s).`
      );
      await refreshList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <Shield className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-300" />
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
              Admin Review Queue
            </h1>
            <p className="mt-1 text-sm text-secondary-700 dark:text-secondary-300">
              Internal ops console for manual report review. Not visible to customers.
            </p>
          </div>
        </div>
      </div>

      <div className="card space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-secondary-900 dark:text-white">
          <KeyRound className="h-4 w-4" />
          Admin key
          {adminKeyReady ? (
            <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900/40 dark:text-green-200">
              Configured (session)
            </span>
          ) : (
            <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900/40 dark:text-red-200">
              Missing
            </span>
          )}
        </div>

        {!adminKeyReady ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="password"
              autoComplete="off"
              placeholder="Paste SCOUTLYTICS admin API key"
              value={adminKeyInput}
              onChange={(e) => setAdminKeyInput(e.target.value)}
              className="input-field flex-1"
            />
            <button type="button" className="btn-primary" onClick={saveAdminKey}>
              Save key
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={removeAdminKey}>
              <Trash2 className="h-4 w-4" />
              Clear key
            </button>
          </div>
        )}

        {!adminKeyReady && (
          <p className="text-sm text-secondary-600 dark:text-secondary-300">
            Admin key missing. Paste your key to load the review queue. Stored in sessionStorage
            only for this browser tab session.
          </p>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          <p>{errorMessage}</p>
          <button type="button" className="mt-2 underline" onClick={refreshList}>
            Retry
          </button>
        </div>
      )}

      {adminKeyReady && (
        <>
          <div className="card space-y-4">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Discover</h2>
            <p className="text-sm text-secondary-600 dark:text-secondary-300">
              Dry-run discover checks completed analyses without writing. Create Missing writes{' '}
              <code className="rounded bg-secondary-100 px-1 dark:bg-secondary-800">review_items</code>{' '}
              documents.
            </p>
            <div className="grid gap-3 md:grid-cols-4">
              <input
                type="text"
                placeholder="Optional upload ID"
                value={discoverUploadId}
                onChange={(e) => setDiscoverUploadId(e.target.value)}
                className="input-field md:col-span-2"
              />
              <select
                value={discoverLimit}
                onChange={(e) => setDiscoverLimit(Number(e.target.value))}
                className="input-field"
              >
                <option value={5}>Limit 5</option>
                <option value={10}>Limit 10</option>
                <option value={25}>Limit 25</option>
              </select>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm disabled:opacity-50"
                  disabled={actionLoading}
                  onClick={() => runDiscover(true, false)}
                >
                  Dry-run Discover
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                  disabled={actionLoading}
                  onClick={() => runDiscover(false, true)}
                >
                  Create Missing
                </button>
              </div>
            </div>
            {discoverResult ? (
              <p className="text-sm text-secondary-700 dark:text-secondary-300">{discoverResult}</p>
            ) : null}
          </div>

          <AdminReviewFilters
            filters={filters}
            onChange={setFilters}
            onRefresh={refreshList}
            loading={listLoading}
          />

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(20rem,0.9fr)]">
            <AdminReviewList
              items={visibleItems}
              selectedUploadId={selectedUploadId}
              onSelect={handleSelect}
              loading={listLoading}
            />
            <AdminReviewDetailPanel
              item={selectedItem}
              loading={detailLoading}
              actionLoading={actionLoading}
              lastScanResult={lastScanResult}
              lastEvaluateSummary={lastEvaluateSummary}
              onRunEvaluate={runEvaluate}
              onRunScan={runScan}
              onClaim={runClaim}
              onOpenDecision={openDecisionModal}
              onMarkDelivered={confirmMarkDelivered}
              onRequestFeedback={confirmRequestFeedback}
            />
          </div>
        </>
      )}

      <AdminReviewDecisionModal
        open={decisionModalOpen}
        decision={pendingDecision}
        uploadId={selectedUploadId || undefined}
        submitting={actionLoading}
        onClose={() => {
          setDecisionModalOpen(false);
          setPendingDecision(null);
        }}
        onSubmit={submitDecision}
      />
    </div>
  );
};

export default AdminReviewQueue;
